/**
 * PATH: apps/ai/src/pages/dashboard/MetricCard.tsx
 * PURPOSE: MiniSparkline and MetricCard components for the Intelligence sidebar
 */

import { TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// MiniSparkline
// ---------------------------------------------------------------------------

export function MiniSparkline({ data = [], color = "currentColor", height = 24 }: { data: number[]; color?: string; height?: number }) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const width = 48;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MetricCard
// ---------------------------------------------------------------------------

const statusColors = {
  good: "border-emerald-500/30 hover:border-emerald-500/50 bg-emerald-500/5",
  warning: "border-amber-500/30 hover:border-amber-500/50 bg-amber-500/5",
  critical: "border-red-500/30 hover:border-red-500/50 bg-red-500/5",
  neutral: "border-border hover:border-primary/30 bg-card",
};

const iconColors = {
  good: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  critical: "text-red-600 dark:text-red-400",
  neutral: "text-muted-foreground",
};

const sparklineColors = {
  good: "#10b981",
  warning: "#f59e0b",
  critical: "#ef4444",
  neutral: "#0d9488",
};

interface MetricCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  status?: 'good' | 'warning' | 'critical' | 'neutral';
  trend?: number;
  sparkline?: number[];
  onClick?: () => void;
  loading?: boolean;
}

export function MetricCard({ icon: Icon, label, value, status = "neutral", trend, sparkline, onClick, loading = false }: MetricCardProps) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={cn(
        "group relative p-3 rounded-xl border transition-all duration-200",
        "hover:shadow-md active:scale-[0.98] text-left w-full",
        statusColors[status],
        loading && "opacity-60 cursor-wait",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon className={cn("w-3.5 h-3.5", iconColors[status])} />
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            {loading ? (
              <div className="h-6 w-16 bg-accent animate-pulse rounded" />
            ) : (
              <>
                <span className={cn(
                  "text-lg font-bold tabular-nums",
                  status === "critical" ? "text-red-600 dark:text-red-400" :
                  status === "warning" ? "text-amber-600 dark:text-amber-400" :
                  status === "good" ? "text-emerald-600 dark:text-emerald-400" :
                  "text-foreground",
                )}>
                  {value}
                </span>
                {trend !== undefined && (
                  <span className={cn("text-[10px] font-medium flex items-center", trend > 0 ? "text-emerald-600" : "text-red-600")}>
                    {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {Math.abs(trend).toFixed(0)}%
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {sparkline && !loading && <MiniSparkline data={sparkline} height={24} color={sparklineColors[status]} />}
      </div>
    </button>
  );
}
